import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { MinusIcon, PlusIcon, LocateFixedIcon } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
    clampCenter,
    clampZoom,
    fitView,
    gtaToWorld,
    screenToGta,
    screenToWorld,
    tileStyles,
    visibleTiles,
    type MapView,
    type TileStyle,
} from './mapProjection';

const DRAG_THRESHOLD = 4;
const WHEEL_STEP = 0.5;
const TILE_STYLE_KEY = 'vantaLiveMap:tileStyle';

const loadTileStyle = (): TileStyle => {
    try {
        const v = localStorage.getItem(TILE_STYLE_KEY);
        if (v && v in tileStyles) return v as TileStyle;
    } catch (_) { }
    return 'atlas';
};

export type MapPointerInfo = {
    x: number;
    y: number;
    sx: number;
    sy: number;
    view: MapView;
};

export type MapFocusRequest = {
    x: number;
    y: number;
    zoom?: number;
    seq: number;
};

type Props = {
    children?: (view: MapView) => ReactNode;
    onMapClick?: (info: MapPointerInfo) => void;
    onMapHover?: (info: MapPointerInfo | null) => void;
    /** texte additionnel affiché à côté des coordonnées du curseur */
    statusText?: string;
    focus?: MapFocusRequest | null;
    className?: string;
};


/**
 * Carte de San Andreas maison (pas de Leaflet dans le panel) :
 * tuiles gtamap.xyz, zoom molette/boutons, déplacement à la souris.
 * Les calques sont rendus par `children(view)` au-dessus des tuiles.
 */
export default function GtaMap({ children, onMapClick, onMapHover, statusText, focus, className }: Props) {
    const containerRef = useRef<HTMLDivElement>(null);
    const [view, setView] = useState<MapView | null>(null);
    const viewRef = useRef<MapView | null>(null);
    viewRef.current = view;
    const [tileStyle, setTileStyle] = useState<TileStyle>(loadTileStyle);
    const [cursor, setCursor] = useState<{ x: number, y: number } | null>(null);
    const [isDragging, setIsDragging] = useState(false);
    const dragRef = useRef<{ startX: number, startY: number, cx: number, cy: number, active: boolean, pointerId: number } | null>(null);

    //Taille du conteneur
    useLayoutEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const ro = new ResizeObserver(() => {
            const width = el.clientWidth;
            const height = el.clientHeight;
            if (!width || !height) return;
            setView((prev) => prev ? { ...prev, width, height } : fitView(width, height));
        });
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    //Centrage demandé par le parent
    useEffect(() => {
        if (!focus) return;
        setView((prev) => {
            if (!prev) return prev;
            const { px, py } = gtaToWorld(focus.x, focus.y);
            const { cx, cy } = clampCenter(px, py);
            return { ...prev, cx, cy, zoom: clampZoom(focus.zoom ?? Math.max(prev.zoom, 5)) };
        });
    }, [focus?.seq]);

    const zoomAt = useCallback((delta: number, sx?: number, sy?: number) => {
        setView((prev) => {
            if (!prev) return prev;
            const zoom = clampZoom(prev.zoom + delta);
            if (zoom === prev.zoom) return prev;
            const ax = sx ?? prev.width / 2;
            const ay = sy ?? prev.height / 2;
            //garde le point sous le curseur immobile
            const anchor = screenToWorld(prev, ax, ay);
            const s = 2 ** zoom;
            const { cx, cy } = clampCenter(
                anchor.px - (ax - prev.width / 2) / s,
                anchor.py - (ay - prev.height / 2) / s,
            );
            return { ...prev, zoom, cx, cy };
        });
    }, []);

    //Molette (listener non passif pour pouvoir bloquer le scroll de la page)
    useEffect(() => {
        const el = containerRef.current;
        if (!el) return;
        const onWheel = (e: WheelEvent) => {
            e.preventDefault();
            const rect = el.getBoundingClientRect();
            zoomAt(e.deltaY < 0 ? WHEEL_STEP : -WHEEL_STEP, e.clientX - rect.left, e.clientY - rect.top);
        };
        el.addEventListener('wheel', onWheel, { passive: false });
        return () => el.removeEventListener('wheel', onWheel);
    }, [zoomAt]);

    const pointerInfo = (e: React.PointerEvent | React.MouseEvent): MapPointerInfo | null => {
        const v = viewRef.current;
        const el = containerRef.current;
        if (!v || !el) return null;
        const rect = el.getBoundingClientRect();
        const sx = e.clientX - rect.left;
        const sy = e.clientY - rect.top;
        const { x, y } = screenToGta(v, sx, sy);
        return { x, y, sx, sy, view: v };
    };

    const onPointerDown = (e: React.PointerEvent) => {
        if (e.button !== 0 || !view) return;
        dragRef.current = {
            startX: e.clientX,
            startY: e.clientY,
            cx: view.cx,
            cy: view.cy,
            active: false,
            pointerId: e.pointerId,
        };
    };

    const onPointerMove = (e: React.PointerEvent) => {
        const info = pointerInfo(e);
        if (info) {
            setCursor({ x: info.x, y: info.y });
        }
        const drag = dragRef.current;
        if (drag) {
            const dx = e.clientX - drag.startX;
            const dy = e.clientY - drag.startY;
            if (!drag.active && Math.hypot(dx, dy) > DRAG_THRESHOLD) {
                drag.active = true;
                setIsDragging(true);
                //capture seulement une fois le déplacement commencé, sinon les clics sur les marqueurs sont perdus
                try { containerRef.current?.setPointerCapture(drag.pointerId); } catch (_) { }
            }
            if (drag.active) {
                setView((prev) => {
                    if (!prev) return prev;
                    const s = 2 ** prev.zoom;
                    const { cx, cy } = clampCenter(drag.cx - dx / s, drag.cy - dy / s);
                    return { ...prev, cx, cy };
                });
                return;
            }
        }
        if (info) onMapHover?.(info);
    };

    const onPointerUp = (e: React.PointerEvent) => {
        const drag = dragRef.current;
        dragRef.current = null;
        if (!drag) return;
        if (drag.active) {
            setIsDragging(false);
            try { containerRef.current?.releasePointerCapture(drag.pointerId); } catch (_) { }
            return;
        }
        //clic simple sur le fond de carte
        if (e.target === e.currentTarget || (e.target as HTMLElement).dataset?.mapLayer) {
            const info = pointerInfo(e);
            if (info) onMapClick?.(info);
        }
    };

    const onPointerLeave = () => {
        setCursor(null);
        onMapHover?.(null);
    };

    const changeTileStyle = (style: TileStyle) => {
        setTileStyle(style);
        try { localStorage.setItem(TILE_STYLE_KEY, style); } catch (_) { }
    };

    const tiles = useMemo(() => (view ? visibleTiles(view, tileStyle) : []), [view, tileStyle]);

    return (
        <div
            ref={containerRef}
            className={cn(
                'relative overflow-hidden select-none touch-none',
                isDragging ? 'cursor-grabbing' : 'cursor-grab',
                className,
            )}
            onPointerDown={onPointerDown}
            onPointerMove={onPointerMove}
            onPointerUp={onPointerUp}
            onPointerLeave={onPointerLeave}
            style={{ backgroundColor: tileStyles[tileStyle].ocean }}
            data-map-layer="root"
        >
            {/* Tuiles */}
            <div className="absolute inset-0 pointer-events-none" data-map-layer="tiles">
                {tiles.map((t) => (
                    <img
                        key={t.key}
                        src={t.url}
                        alt=""
                        draggable={false}
                        className="absolute max-w-none"
                        style={{ left: t.left, top: t.top, width: t.size, height: t.size }}
                        onError={(e) => { (e.target as HTMLImageElement).style.visibility = 'hidden'; }}
                    />
                ))}
            </div>

            {/* Calques */}
            {view && children?.(view)}

            {/* Zoom */}
            <div className="absolute top-3 left-3 z-20 flex flex-col rounded-md border bg-card overflow-hidden"
                onPointerDown={(e) => e.stopPropagation()}
            >
                <button type="button" title="Zoom in" className="p-1.5 hover:bg-secondary" onClick={() => zoomAt(1)}>
                    <PlusIcon className="size-4" />
                </button>
                <button type="button" title="Zoom out" className="p-1.5 border-t hover:bg-secondary" onClick={() => zoomAt(-1)}>
                    <MinusIcon className="size-4" />
                </button>
                <button
                    type="button"
                    title="Reset view"
                    className="p-1.5 border-t hover:bg-secondary"
                    onClick={() => setView((prev) => prev ? fitView(prev.width, prev.height) : prev)}
                >
                    <LocateFixedIcon className="size-4" />
                </button>
            </div>

            {/* Choix des tuiles */}
            <div className="absolute top-3 right-3 z-20 flex rounded-md border bg-card text-xs overflow-hidden"
                onPointerDown={(e) => e.stopPropagation()}
            >
                {(Object.keys(tileStyles) as TileStyle[]).map((style) => (
                    <button
                        key={style}
                        type="button"
                        className={cn(
                            'px-2.5 py-1.5',
                            style === tileStyle ? 'bg-secondary text-secondary-foreground' : 'text-muted-foreground hover:bg-muted',
                        )}
                        onClick={() => changeTileStyle(style)}
                    >
                        {tileStyles[style].label}
                    </button>
                ))}
            </div>

            {/* Coordonnées du curseur */}
            <div className="absolute bottom-3 left-3 z-20 rounded-md border bg-card px-2 py-1 font-mono text-xs text-muted-foreground pointer-events-none">
                {cursor
                    ? `x: ${cursor.x.toFixed(1)}  y: ${cursor.y.toFixed(1)}`
                    : 'x: —  y: —'}
                {statusText ? <span className="ml-3 text-primary">{statusText}</span> : null}
            </div>
        </div>
    );
}
